import React, { useState, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { Calculator, Planet, Lightning, Handshake, Play } from '@phosphor-icons/react';
import { formatMoney } from '../utils/formatMoney';

const RANKS = [
  { id: 'mercure', fr: 'Mercure', en: 'Mercury', orbits: 12, fsLevels: 1, matchGens: 1 },
  { id: 'venus', fr: 'Venus', en: 'Venus', orbits: 13, fsLevels: 2, matchGens: 1 },
  { id: 'terre', fr: 'Terre', en: 'Earth', orbits: 13, fsLevels: 4, matchGens: 2 },
  { id: 'mars', fr: 'Mars', en: 'Mars', orbits: 14, fsLevels: 6, matchGens: 3 },
  { id: 'jupiter', fr: 'Jupiter', en: 'Jupiter', orbits: 14, fsLevels: 8, matchGens: 4 },
  { id: 'saturne', fr: 'Saturne', en: 'Saturn', orbits: 15, fsLevels: 10, matchGens: 5 },
];

const FS_RATES = { 1: 0.50, 2: 0.10, 3: 0.05, 4: 0.05, 5: 0.03, 6: 0.02, 7: 0.02, 8: 0.01, 9: 0.01, 10: 0.01 };
const MATCH_RATES = { 1: 0.50, 2: 0.10, 3: 0.05, 4: 0.05, 5: 0.03 };

const OrbitCalculatorPage = () => {
  const { language } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;

  // Editable parameters
  const [rank, setRank] = useState('saturne');
  const [membership, setMembership] = useState(9.95);
  const [commRate, setCommRate] = useState(2.5);
  const [affiliateFee, setAffiliateFee] = useState(49.95);
  const [fillPercent, setFillPercent] = useState(100);
  const [newRecruits, setNewRecruits] = useState(10);

  const selectedRank = RANKS.find(r => r.id === rank) || RANKS[5];

  const [results, setResults] = useState(null);
  const [calculated, setCalculated] = useState(false);

  const calculate = useCallback(() => {
    const rateDecimal = commRate / 100;
    const commPerMember = membership * rateDecimal;
    const maxOrbits = selectedRank.orbits;
    const fillFactor = fillPercent / 100;

    // Matrix commissions per orbit
    const orbitDetails = [];
    let totalMatrixMonthly = 0;

    for (let orbit = 1; orbit <= maxOrbits; orbit++) {
      const maxMembers = Math.pow(2, orbit);
      const activeMembers = Math.round(maxMembers * fillFactor);
      const orbitCommission = activeMembers * commPerMember;
      totalMatrixMonthly += orbitCommission;
      orbitDetails.push({ orbit, maxMembers, activeMembers, commPerMember, orbitCommission });
    }

    // Fast Start calculation
    const fsBonusPerRecruit = 5.00 + 20.00;
    let totalFastStart = 0;
    const fsDetails = [];
    for (let level = 1; level <= selectedRank.fsLevels; level++) {
      const rate = FS_RATES[level] || 0;
      const amount = fsBonusPerRecruit * rate * newRecruits;
      totalFastStart += amount;
      fsDetails.push({ level, rate, amount, perRecruit: fsBonusPerRecruit * rate });
    }

    // Matching Bonus estimation
    const avgDownlineMatrixEarnings = totalMatrixMonthly * 0.3;
    let totalMatching = 0;
    const matchDetails = [];
    for (let gen = 1; gen <= selectedRank.matchGens; gen++) {
      const rate = MATCH_RATES[gen] || 0;
      const amount = avgDownlineMatrixEarnings * rate;
      totalMatching += amount;
      matchDetails.push({ gen, rate, amount });
    }

    const totalMonthly = totalMatrixMonthly + totalMatching;
    const totalWithFS = totalMonthly + totalFastStart;

    let totalNetwork = 0;
    for (let orbit = 1; orbit <= maxOrbits; orbit++) {
      totalNetwork += Math.round(Math.pow(2, orbit) * fillFactor);
    }

    setResults({
      orbitDetails, totalMatrixMonthly, fsDetails, totalFastStart,
      matchDetails, totalMatching, totalMonthly, totalWithFS, totalNetwork, commPerMember,
    });
    setCalculated(true);
  }, [rank, membership, commRate, fillPercent, newRecruits, selectedRank]);

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-calculator-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Calculator className="w-8 h-8 text-indigo-400" />
            {t('Calculatrice de scenarios', 'Scenario Calculator')}
          </h1>
          <p className="text-gray-300 text-sm mt-1">{t('Simulez vos revenus selon differents parametres', 'Simulate your earnings with different parameters')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Parameters Panel */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-5 space-y-5">
              <h2 className="text-lg font-semibold text-white">{t('Parametres', 'Parameters')}</h2>

              {/* Rank */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">{t('Votre rang', 'Your rank')}</label>
                <select value={rank} onChange={e => setRank(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm"
                  data-testid="calc-rank">
                  {RANKS.map(r => (
                    <option key={r.id} value={r.id}>{language === 'fr' ? r.fr : r.en} ({r.orbits} {t('orbites', 'orbits')})</option>
                  ))}
                </select>
              </div>

              {/* Membership */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">{t('Prix abonnement mensuel', 'Monthly membership price')}</label>
                <div className="relative">
                  <input type="number" step="0.01" min="0" value={membership} onChange={e => setMembership(Number(e.target.value))}
                    className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-8"
                    data-testid="calc-membership" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                </div>
              </div>

              {/* Commission Rate */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">{t('Taux de commission par orbite', 'Commission rate per orbit')}</label>
                <div className="relative">
                  <input type="number" step="0.1" min="0" max="100" value={commRate} onChange={e => setCommRate(Number(e.target.value))}
                    className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-8"
                    data-testid="calc-comm-rate" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
              </div>

              {/* Affiliate Fee */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">{t("Frais d'affiliation", 'Affiliate fee')}</label>
                <div className="relative">
                  <input type="number" step="0.01" min="0" value={affiliateFee} onChange={e => setAffiliateFee(Number(e.target.value))}
                    className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm pr-8"
                    data-testid="calc-affiliate-fee" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                </div>
              </div>

              {/* Fill % */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  {t('Taux de remplissage', 'Fill rate')}: <span className="text-indigo-400 font-bold">{fillPercent}%</span>
                </label>
                <input type="range" min="1" max="100" value={fillPercent} onChange={e => setFillPercent(Number(e.target.value))}
                  className="w-full accent-indigo-500" data-testid="calc-fill-percent" />
                <div className="flex justify-between text-xs text-gray-400"><span>1%</span><span>50%</span><span>100%</span></div>
              </div>

              {/* New recruits for FS */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">{t('Nouveaux recrutes (Fast Start)', 'New recruits (Fast Start)')}</label>
                <input type="number" min="0" value={newRecruits} onChange={e => setNewRecruits(Number(e.target.value))}
                  className="w-full px-3 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm"
                  data-testid="calc-new-recruits" />
              </div>

              {/* CALCULER BUTTON */}
              <button onClick={calculate}
                className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-500 hover:to-purple-500 transition-all shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2 text-base"
                data-testid="calc-calculate-btn">
                <Play className="w-5 h-5" weight="fill" />
                {t('Calculer', 'Calculate')}
              </button>
            </div>

            {/* Summary Card - only shows after calculation */}
            {calculated && results && (
            <div className="bg-gradient-to-br from-indigo-600/30 to-purple-600/20 border border-indigo-500/30 rounded-xl p-5 space-y-3">
              <h3 className="text-sm text-indigo-300/70 uppercase tracking-wider">{t('Resume', 'Summary')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">{t('Reseau total', 'Total Network')}</span>
                  <span className="text-white font-bold" data-testid="calc-total-network">{results.totalNetwork.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">{t('Orbites actives', 'Active Orbits')}</span>
                  <span className="text-white font-bold">{selectedRank.orbits}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">{t('Commission/membre', 'Comm./member')}</span>
                  <span className="text-white font-bold">{formatMoney(results.commPerMember, language)}</span>
                </div>
              </div>
              <div className="border-t border-indigo-500/20 pt-3 mt-3">
                <p className="text-indigo-300/60 text-xs">{t('Revenu mensuel total estime', 'Estimated total monthly revenue')}</p>
                <p className="text-3xl font-bold text-white mt-1" data-testid="calc-total-monthly">{formatMoney(results.totalMonthly, language)}</p>
                <p className="text-indigo-300/40 text-xs mt-1">+ Fast Start: {formatMoney(results.totalFastStart, language)}</p>
              </div>
            </div>
            )}
          </div>

          {/* Results Panel - only shows after calculation */}
          {calculated && results ? (
          <div className="lg:col-span-2 space-y-5">

            {/* Matrix Commissions Table */}
            <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 p-4 border-b border-indigo-500/10">
                <Planet className="w-5 h-5 text-indigo-400" />
                <h2 className="text-lg font-semibold text-white">{t('Commissions Matrice par orbite', 'Matrix Commissions by orbit')}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs uppercase border-b border-indigo-500/10">
                    <tr>
                      <th className="px-4 py-3 text-left">{t('Orbite', 'Orbit')}</th>
                      <th className="px-4 py-3 text-right">{t('Places max', 'Max slots')}</th>
                      <th className="px-4 py-3 text-right">{t('Membres actifs', 'Active members')}</th>
                      <th className="px-4 py-3 text-right">{t('Comm./membre', 'Comm./member')}</th>
                      <th className="px-4 py-3 text-right">{t('Total orbite', 'Orbit total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-500/10">
                    {results.orbitDetails.map(row => (
                      <tr key={row.orbit} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2.5">
                          <span className="text-indigo-400 font-medium">{row.orbit}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-400">{row.maxMembers.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right text-white">{row.activeMembers.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right text-gray-400">{formatMoney(row.commPerMember, language)}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-400 font-medium">{formatMoney(row.orbitCommission, language)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-indigo-500/20">
                    <tr className="bg-indigo-500/5">
                      <td colSpan="2" className="px-4 py-3 text-white font-semibold">{t('Total Matrice mensuel', 'Total monthly Matrix')}</td>
                      <td className="px-4 py-3 text-right text-white font-bold">{results.totalNetwork.toLocaleString()}</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right text-emerald-400 font-bold text-lg" data-testid="calc-matrix-total">
                        {formatMoney(results.totalMatrixMonthly, language)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Fast Start & Matching side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Fast Start */}
              <div className="bg-[#111827]/80 border border-emerald-500/10 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 p-4 border-b border-emerald-500/10">
                  <Lightning className="w-5 h-5 text-emerald-400" />
                  <h2 className="font-semibold text-white">Fast Start</h2>
                  <span className="ml-auto text-xs text-gray-500">{newRecruits} {t('recrutes', 'recruits')}</span>
                </div>
                <div className="p-4 space-y-2">
                  {results.fsDetails.map(row => (
                    <div key={row.level} className="flex justify-between items-center py-1.5">
                      <span className="text-gray-400 text-sm">{t('Niveau', 'Level')} {row.level} <span className="text-gray-400">({(row.rate * 100).toFixed(0)}%)</span></span>
                      <span className="text-white text-sm font-medium">{formatMoney(row.amount, language)}</span>
                    </div>
                  ))}
                  {results.fsDetails.length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-2">{t('Aucun niveau debloque', 'No levels unlocked')}</p>
                  )}
                  <div className="flex justify-between items-center py-2 mt-2 border-t border-emerald-500/10">
                    <span className="text-emerald-400 font-semibold text-sm">Total Fast Start</span>
                    <span className="text-emerald-400 font-bold" data-testid="calc-fs-total">{formatMoney(results.totalFastStart, language)}</span>
                  </div>
                </div>
              </div>

              {/* Matching Bonus */}
              <div className="bg-[#111827]/80 border border-purple-500/10 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 p-4 border-b border-purple-500/10">
                  <Handshake className="w-5 h-5 text-purple-400" />
                  <h2 className="font-semibold text-white">Matching Bonus</h2>
                </div>
                <div className="p-4 space-y-2">
                  {results.matchDetails.map(row => (
                    <div key={row.gen} className="flex justify-between items-center py-1.5">
                      <span className="text-gray-400 text-sm">{t('Generation', 'Generation')} {row.gen} <span className="text-gray-400">({(row.rate * 100).toFixed(0)}%)</span></span>
                      <span className="text-white text-sm font-medium">{formatMoney(row.amount, language)}</span>
                    </div>
                  ))}
                  {results.matchDetails.length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-2">{t('Aucune generation debloquee', 'No generations unlocked')}</p>
                  )}
                  <div className="flex justify-between items-center py-2 mt-2 border-t border-purple-500/10">
                    <span className="text-purple-400 font-semibold text-sm">Total Matching</span>
                    <span className="text-purple-400 font-bold" data-testid="calc-match-total">{formatMoney(results.totalMatching, language)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Grand Total */}
            <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-emerald-600/20 border border-indigo-500/20 rounded-xl p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">{t('Matrice mensuel', 'Monthly Matrix')}</p>
                  <p className="text-xl font-bold text-indigo-400 mt-1">{formatMoney(results.totalMatrixMonthly, language)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Matching Bonus</p>
                  <p className="text-xl font-bold text-purple-400 mt-1">{formatMoney(results.totalMatching, language)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Fast Start</p>
                  <p className="text-xl font-bold text-emerald-400 mt-1">{formatMoney(results.totalFastStart, language)}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 text-center">
                <p className="text-gray-400 text-sm">{t('Revenu total estime (mensuel + fast start)', 'Total estimated revenue (monthly + fast start)')}</p>
                <p className="text-4xl font-bold text-white mt-2" data-testid="calc-grand-total">{formatMoney(results.totalWithFS, language)}</p>
              </div>
            </div>
          </div>
          ) : (
          <div className="lg:col-span-2 flex items-center justify-center">
            <div className="text-center py-16 px-8">
              <Calculator className="w-16 h-16 text-indigo-500/20 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('Ajustez les parametres et cliquez sur', 'Adjust the parameters and click')}</p>
              <p className="text-indigo-400 font-bold text-xl mt-1">{t('Calculer', 'Calculate')}</p>
              <p className="text-gray-400 text-sm mt-3">{t('pour voir les resultats de votre scenario', 'to see your scenario results')}</p>
            </div>
          </div>
          )}
        </div>
      </div>
    </OrbitLayout>
  );
};

export default OrbitCalculatorPage;
